import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { 
  Plus,
  Trash2,
  Package,
  Edit2,
  Check,
  X,
  ShieldOff,
  Save
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { useTheme } from "@/context/ThemeContext";

const BASE_API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EditInvoice() {
  const { id, businessSlug } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const [customers, setCustomers] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [invoice, setInvoice] = useState(null);
  
  const API = `${BASE_API}/b/${businessSlug}`;
  const basePath = `/${businessSlug}`;
  
  const [formData, setFormData] = useState({
    customer_id: "",
    customer_name: "",
    company_name: "",
    vehicle_info: "",
    truck_number: "",
    items: [],
    tax_rate: 0.075,
    tax_exempt: false,
    card_fee_enabled: false,
    notes: "",
    due_date: "",
    showCustomerSuggestions: false
  });

  // For editing items inline
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingItem, setEditingItem] = useState(null);

  // QuickBooks-style single row input
  const [newItemDescription, setNewItemDescription] = useState("");
  const [newItemType, setNewItemType] = useState("part");
  const [newItemQty, setNewItemQty] = useState(1);
  const [newItemRate, setNewItemRate] = useState("");
  const [selectedInventoryId, setSelectedInventoryId] = useState("");

  const [showPartDropdown, setShowPartDropdown] = useState(false);
  // Per-business line-item categories so custom types (tire, material, …) show
  // up in BOTH the "add new line" Select and the per-row edit Select. Without
  // this, editing an invoice that uses a custom category would either silently
  // change the type or fail to render.
  const [lineItemTypes, setLineItemTypes] = useState([
    { key: "part", label: "Part", track_inventory: true, is_default: true },
    { key: "labor", label: "Labor", track_inventory: false, is_default: false },
  ]);
  const tracksInventory = (key) => {
    const row = lineItemTypes.find((t) => t.key === key);
    if (row) return !!row.track_inventory;
    return key === "part";
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [invoiceRes, customersRes, inventoryRes, settingsRes, lineTypesRes] = await Promise.all([
        axios.get(`${API}/invoices/${id}`),
        axios.get(`${API}/customers`),
        axios.get(`${API}/inventory`),
        axios.get(`${API}/settings`),
        axios.get(`${API}/line-item-types`),
      ]);

      const inv = invoiceRes.data;
      setInvoice(inv);
      setCustomers(customersRes.data);
      setInventory(inventoryRes.data);
      setSettings(settingsRes.data);
      setLineItemTypes(lineTypesRes.data?.line_item_types || []);
      
      // Populate form with existing invoice data
      setFormData({
        customer_id: inv.customer_id || "",
        customer_name: inv.customer_name || "",
        company_name: inv.company_name || "",
        vehicle_info: inv.vehicle_info || "",
        truck_number: inv.truck_number || "",
        items: inv.items || [],
        tax_rate: inv.tax_rate || 0.075,
        tax_exempt: inv.tax_exempt || false,
        card_fee_enabled: inv.card_fee_enabled !== undefined ? inv.card_fee_enabled : false,
        notes: inv.notes || "",
        due_date: inv.due_date ? inv.due_date.split('T')[0] : "",
        showCustomerSuggestions: false
      });
    } catch (error) {
      toast.error("Failed to fetch invoice data");
      navigate(`${basePath}/invoices`);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.items.length === 0) {
      toast.error("Please add at least one item");
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.put(`${API}/invoices/${id}`, formData);
      toast.success("Invoice updated successfully");
      navigate(`${basePath}/invoices/${id}`);
    } catch (error) {
      toast.error("Failed to update invoice");
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const calculateSubtotal = () => {
    return formData.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
  };

  const calculateTax = () => {
    if (formData.tax_exempt || formData.tax_rate === 0) return 0;
    return calculateSubtotal() * formData.tax_rate;
  };

  const calculateCardFee = () => {
    if (!formData.card_fee_enabled) return 0;
    return (calculateSubtotal() + calculateTax()) * 0.03;
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateTax() + calculateCardFee();
  };

  const filteredParts = inventory.filter(item => 
    item.name.toLowerCase().includes(newItemDescription.toLowerCase()) ||
    item.part_number.toLowerCase().includes(newItemDescription.toLowerCase())
  );

  const addNewItem = () => {
    const rate = parseFloat(newItemRate) || 0;
    // For labor, description is optional. For parts, description is required.
    if (tracksInventory(newItemType) && !newItemDescription) {
      toast.error("Please enter a description for this item");
      return;
    }
    if (rate <= 0) {
      toast.error("Please enter a valid rate");
      return;
    }

    const item = {
      type: newItemType,
      description: newItemDescription || "",
      quantity: newItemQty,
      unit_price: rate,
      taxable: !formData.tax_exempt,
      inventory_id: selectedInventoryId
    };

    setFormData({
      ...formData,
      items: [...formData.items, item]
    });

    // Reset inputs
    setNewItemDescription("");
    setNewItemQty(1);
    setNewItemRate("");
    setSelectedInventoryId("");
  };

  const removeItem = (index) => {
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index)
    });
  };

  const startEditItem = (index) => {
    setEditingIndex(index);
    setEditingItem({ ...formData.items[index] });
  };

  const saveEditItem = () => {
    if (!editingItem.description || editingItem.unit_price <= 0) {
      toast.error("Invalid item data");
      return;
    }
    const newItems = [...formData.items];
    newItems[editingIndex] = editingItem;
    setFormData({ ...formData, items: newItems });
    setEditingIndex(null);
    setEditingItem(null);
  };

  const cancelEditItem = () => {
    setEditingIndex(null);
    setEditingItem(null);
  };

  const selectedCustomer = customers.find(c => c.id === formData.customer_id);

  if (loading) {
    return (
      <div className={`flex items-center justify-center h-64 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
        Loading invoice...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>
            Edit Invoice {invoice?.invoice_number}
          </h1>
          <p className={`${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
            Modify invoice details and items
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Customer & Items */}
          <div className="lg:col-span-2 space-y-6">
            {/* Customer Selection */}
            <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
              <CardHeader className={`pb-3 border-b ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
                <CardTitle className={`text-lg ${isDark ? 'text-white' : 'text-slate-800'}`}>Customer</CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Customer Name</Label>
                    <div className="relative">
                      <Input
                        value={formData.customer_name || ''}
                        onChange={(e) => {
                          setFormData({ ...formData, customer_name: e.target.value, showCustomerSuggestions: true });
                        }}
                        onFocus={() => setFormData({ ...formData, showCustomerSuggestions: true })}
                        onBlur={() => setTimeout(() => setFormData(prev => ({ ...prev, showCustomerSuggestions: false })), 200)}
                        placeholder="Type customer name or select..."
                        className={isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}
                        data-testid="customer-name-input"
                      />
                      {/* Customer Suggestions Dropdown */}
                      {formData.showCustomerSuggestions && formData.customer_name && customers.length > 0 && (
                        <div className={`absolute z-50 w-full mt-1 rounded-md shadow-lg max-h-48 overflow-y-auto ${isDark ? 'bg-slate-800 border border-slate-700' : 'bg-white border border-slate-200'}`}>
                          {customers
                            .filter(c => 
                              c.name?.toLowerCase().includes((formData.customer_name || '').toLowerCase()) ||
                              c.company_name?.toLowerCase().includes((formData.customer_name || '').toLowerCase())
                            )
                            .slice(0, 8)
                            .map(customer => (
                              <div
                                key={customer.id}
                                className={`px-3 py-2 cursor-pointer ${isDark ? 'hover:bg-slate-700 text-slate-200' : 'hover:bg-slate-100 text-slate-800'}`}
                                onMouseDown={() => {
                                  setFormData({ 
                                    ...formData, 
                                    customer_id: customer.id, 
                                    customer_name: customer.name,
                                    company_name: customer.company_name || '',
                                    showCustomerSuggestions: false 
                                  });
                                }}
                              >
                                <div className="font-medium">{customer.name}</div>
                                {customer.company_name && (
                                  <div className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{customer.company_name}</div>
                                )}
                              </div>
                            ))
                          }
                          {customers.filter(c => 
                            c.name?.toLowerCase().includes((formData.customer_name || '').toLowerCase()) ||
                            c.company_name?.toLowerCase().includes((formData.customer_name || '').toLowerCase())
                          ).length === 0 && (
                            <div className={`px-3 py-2 text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                              No existing customers match. New name will be used.
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                      Type any name or select from existing customers
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Company Name</Label>
                    <Input
                      value={formData.company_name || ''}
                      onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                      placeholder="Company name (optional)"
                      className={isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}
                      data-testid="company-name-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div className="space-y-2">
                    <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Truck #</Label>
                    <Input
                      value={formData.truck_number}
                      onChange={(e) => setFormData({ ...formData, truck_number: e.target.value })}
                      placeholder="Truck number"
                      className={isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}
                      data-testid="truck-number-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Vehicle Info</Label>
                    <Input
                      value={formData.vehicle_info}
                      onChange={(e) => setFormData({ ...formData, vehicle_info: e.target.value })}
                      placeholder="Year, Make, Model"
                      className={isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}
                      data-testid="vehicle-info-input"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Line Items */}
            <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
              <CardHeader className={`pb-3 border-b ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
                <CardTitle className={`text-lg flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-800'}`}>
                  <Package className="w-5 h-5 text-blue-600" />
                  Line Items
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {/* Table Header */}
                <div className={`grid grid-cols-12 gap-2 px-4 py-2 text-xs font-semibold uppercase tracking-wider ${isDark ? 'bg-slate-700/50 text-slate-400' : 'bg-slate-100 text-slate-500'}`}>
                  <div className="col-span-5">Description</div>
                  <div className="col-span-2">Type</div>
                  <div className="col-span-1 text-center">Qty</div>
                  <div className="col-span-2 text-right">Rate</div>
                  <div className="col-span-2 text-right">Action</div>
                </div>

                {/* Existing Items */}
                {formData.items.map((item, index) => (
                  <div key={index} className={`grid grid-cols-12 gap-2 px-4 py-3 items-center border-b ${isDark ? 'border-slate-700' : 'border-slate-100'}`}>
                    {editingIndex === index ? (
                      <>
                        <div className="col-span-5">
                          <Input
                            value={editingItem.description}
                            onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })}
                            className={`h-8 ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-white border-slate-200'}`}
                          />
                        </div>
                        <div className="col-span-2">
                          <Select value={editingItem.type} onValueChange={(value) => setEditingItem({ ...editingItem, type: value })}>
                            <SelectTrigger className={`h-8 ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-white border-slate-200'}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}>
                              {lineItemTypes.map((t) => (
                                <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-1">
                          <Input
                            type="number"
                            min="0.5"
                            step="0.5"
                            value={editingItem.quantity}
                            onChange={(e) => setEditingItem({ ...editingItem, quantity: parseFloat(e.target.value) || 1 })}
                            className={`h-8 text-center ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-white border-slate-200'}`}
                          />
                        </div>
                        <div className="col-span-2">
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={editingItem.unit_price}
                            onChange={(e) => setEditingItem({ ...editingItem, unit_price: parseFloat(e.target.value) || 0 })}
                            className={`h-8 text-right ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-white border-slate-200'}`}
                          />
                        </div>
                        <div className="col-span-2 flex justify-end gap-1">
                          <Button type="button" size="icon" variant="ghost" onClick={saveEditItem} className="h-7 w-7 text-green-600 hover:bg-green-50">
                            <Check className="w-4 h-4" />
                          </Button>
                          <Button type="button" size="icon" variant="ghost" onClick={cancelEditItem} className="h-7 w-7 text-red-600 hover:bg-red-50">
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className={`col-span-5 ${isDark ? 'text-white' : 'text-slate-800'}`}>{item.description}</div>
                        <div className="col-span-2">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            tracksInventory(item.type)
                              ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                              : 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                          }`}>
                            {lineItemTypes.find((t) => t.key === item.type)?.label || item.type}
                          </span>
                        </div>
                        <div className={`col-span-1 text-center ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{item.quantity}</div>
                        <div className={`col-span-2 text-right ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{formatCurrency(item.unit_price)}</div>
                        <div className="col-span-2 flex justify-end gap-1">
                          <Button type="button" size="icon" variant="ghost" onClick={() => startEditItem(index)} className="h-7 w-7 hover:bg-blue-50 hover:text-blue-600">
                            <Edit2 className="w-3 h-3" />
                          </Button>
                          <Button type="button" size="icon" variant="ghost" onClick={() => removeItem(index)} className="h-7 w-7 hover:bg-red-50 hover:text-red-600">
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                ))}

                {/* Add New Item Row */}
                <div className={`grid grid-cols-12 gap-2 px-4 py-3 items-end ${isDark ? 'bg-slate-700/30' : 'bg-slate-50/50'}`}>
                  <div className="col-span-5 relative">
                    <Input
                      placeholder={tracksInventory(newItemType) ? "Enter item or search inventory..." : "Enter description..."}
                      value={newItemDescription}
                      onChange={(e) => {
                        setNewItemDescription(e.target.value);
                        if (tracksInventory(newItemType)) {
                          setShowPartDropdown(true);
                        }
                      }}
                      onFocus={() => {
                        if (tracksInventory(newItemType)) {
                          setShowPartDropdown(true);
                        }
                      }}
                      onBlur={() => setTimeout(() => setShowPartDropdown(false), 200)}
                      className={`h-9 ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-white border-slate-200'}`}
                      data-testid="new-item-description"
                    />
                    {/* Inventory Dropdown — visible for any inventory-tracked category */}
                    {showPartDropdown && tracksInventory(newItemType) && newItemDescription && filteredParts.length > 0 && (
                      <div className={`absolute z-50 mt-1 w-full rounded border shadow-xl max-h-48 overflow-auto ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                        {filteredParts.slice(0, 8).map((part) => (
                          <div
                            key={part.id}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setNewItemDescription(part.name);
                              setNewItemRate(part.price);
                              setNewItemType("part");
                              setSelectedInventoryId(part.id);
                              setShowPartDropdown(false);
                            }}
                            className={`p-2 cursor-pointer ${isDark ? 'hover:bg-slate-700 border-b border-slate-700' : 'hover:bg-slate-50 border-b border-slate-100'}`}
                          >
                            <div className="flex justify-between text-sm">
                              <span className={isDark ? 'text-white' : 'text-slate-800'}>{part.name}</span>
                              <span className="font-medium text-blue-600">{formatCurrency(part.price)}</span>
                            </div>
                            <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{part.part_number} • {part.quantity} in stock</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="col-span-2">
                    <Select
                      value={newItemType}
                      onValueChange={(value) => {
                        setNewItemType(value);
                        // Reset inventory binding when the new type is NOT
                        // an inventory-tracking category (e.g. labor / service).
                        if (!tracksInventory(value)) {
                          setSelectedInventoryId("");
                          setShowPartDropdown(false);
                        }
                      }}
                    >
                      <SelectTrigger className={`h-9 ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-white border-slate-200'}`} data-testid="new-item-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}>
                        {lineItemTypes.map((t) => (
                          <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-1">
                    <Input
                      type="number"
                      min="0.5"
                      step="0.5"
                      value={newItemQty}
                      onChange={(e) => setNewItemQty(parseFloat(e.target.value) || 1)}
                      className={`h-9 text-center ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-white border-slate-200'}`}
                      data-testid="new-item-qty"
                    />
                  </div>
                  <div className="col-span-2">
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      value={newItemRate}
                      onChange={(e) => setNewItemRate(e.target.value)}
                      className={`h-9 text-right ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-white border-slate-200'}`}
                      data-testid="new-item-rate"
                    />
                  </div>
                  <div className="col-span-2 flex justify-end">
                    <Button
                      type="button"
                      onClick={addNewItem}
                      disabled={!newItemRate || parseFloat(newItemRate) <= 0 || (tracksInventory(newItemType) && !newItemDescription)}
                      className="bg-blue-600 hover:bg-blue-700 text-white h-9 px-4"
                      data-testid="add-item-btn"
                    >
                      <Plus className="w-4 h-4 mr-1" /> Add
                    </Button>
                  </div>
                </div>

                {/* Empty State */}
                {formData.items.length === 0 && (
                  <div className={`text-center py-8 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                    No items added yet. Add parts or labor above.
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Notes */}
            <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
              <CardContent className="pt-4">
                <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Additional notes for this invoice..."
                  className={`mt-2 ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}`}
                  rows={3}
                  data-testid="notes-input"
                />
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Summary */}
          <div className="space-y-6">
            <Card className={`sticky top-4 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
              <CardHeader className={`pb-3 border-b ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
                <CardTitle className={`text-lg ${isDark ? 'text-white' : 'text-slate-800'}`}>Invoice Summary</CardTitle>
              </CardHeader>
              <CardContent className="pt-4 space-y-4">
                {/* Tax Rate - Only show if not tax exempt */}
                {!formData.tax_exempt && (
                <div className="space-y-2">
                  <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Tax Rate</Label>
                  <Select 
                    value={String(formData.tax_rate)} 
                    onValueChange={(value) => setFormData({ ...formData, tax_rate: parseFloat(value) })}
                  >
                    <SelectTrigger className={isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'} data-testid="tax-rate-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}>
                      {(settings?.tax_rates || [0, 6, 7.5, 8, 8.25, 10]).map((rate) => (
                        <SelectItem key={rate} value={String(rate / 100)}>
                          {rate === 0 ? 'No Tax (0%)' : `${rate}%`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                )}

                {/* Tax Exempt Toggle */}
                <div className={`p-3 rounded-lg border ${formData.tax_exempt ? 'bg-amber-50 border-amber-300' : isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <ShieldOff className={`w-4 h-4 ${formData.tax_exempt ? 'text-amber-600' : isDark ? 'text-slate-400' : 'text-slate-500'}`} />
                      <Label className={`font-medium ${formData.tax_exempt ? 'text-amber-700' : isDark ? 'text-slate-300' : 'text-slate-700'}`}>Tax Exempt</Label>
                    </div>
                    <Switch
                      checked={formData.tax_exempt}
                      onCheckedChange={(checked) => {
                        const defaultRate = (settings?.tax_settings?.default_tax_rate || 7.5) / 100;
                        const taxLabor = settings?.tax_settings?.tax_labor === true;
                        // Update items' taxable status when toggling tax exempt
                        // When turning OFF tax exempt, mark all items as taxable (parts always, labor if tax_labor is true)
                        const updatedItems = formData.items.map(item => ({
                          ...item,
                          taxable: checked ? false : (tracksInventory(item.type) ? true : !!taxLabor)
                        }));
                        // Tax exempt toggle
                        setFormData(prev => ({ 
                          ...prev, 
                          tax_exempt: checked,
                          tax_rate: checked ? 0 : defaultRate,
                          items: updatedItems
                        }));
                      }}
                      data-testid="tax-exempt-toggle"
                    />
                  </div>
                  {formData.tax_exempt && (
                    <p className="text-xs text-amber-600 mt-2">No tax will be applied to this invoice</p>
                  )}
                </div>

                {/* Card Fee Toggle */}
                <div className="flex items-center justify-between">
                  <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Card Fee (3%)</Label>
                  <Switch
                    checked={formData.card_fee_enabled}
                    onCheckedChange={(checked) => setFormData({ ...formData, card_fee_enabled: checked })}
                    data-testid="card-fee-toggle"
                  />
                </div>
                {formData.card_fee_enabled && (
                  <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                    3% processing fee will be added if customer pays with card
                  </p>
                )}

                {selectedCustomer?.tax_exempt && (
                  <p className="text-xs text-amber-600 flex items-center gap-1">
                    <ShieldOff className="w-3 h-3" />
                    Tax exempt customer
                  </p>
                )}

                <div className="space-y-2">
                  <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Due Date</Label>
                  <Input
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                    className={isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}
                    data-testid="due-date-input"
                  />
                </div>

                <Separator className={isDark ? 'bg-slate-700' : 'bg-slate-200'} />

                <div className="space-y-2">
                  <div className={`flex justify-between ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                    <span>Subtotal</span>
                    <span>{formatCurrency(calculateSubtotal())}</span>
                  </div>
                  <div className={`flex justify-between ${(formData.tax_exempt || formData.tax_rate === 0) ? 'text-amber-600' : isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                    <span>Tax {(formData.tax_exempt || formData.tax_rate === 0) ? '' : `(${(formData.tax_rate * 100).toFixed(1).replace(/\.0$/, '')}%)`}</span>
                    <span>{(formData.tax_exempt || formData.tax_rate === 0) ? 'TAX EXEMPT' : formatCurrency(calculateTax())}</span>
                  </div>
                  {formData.card_fee_enabled && (
                    <div className={`flex justify-between text-blue-600`}>
                      <span>Card Fee (3%)</span>
                      <span>{formatCurrency(calculateCardFee())}</span>
                    </div>
                  )}
                  <Separator className={isDark ? 'bg-slate-700' : 'bg-slate-200'} />
                  <div className="flex justify-between text-xl font-bold">
                    <span className={isDark ? 'text-white' : 'text-slate-800'}>Total</span>
                    <span className="text-blue-600">{formatCurrency(calculateTotal())}</span>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={submitting || (!formData.customer_id && !formData.customer_name) || formData.items.length === 0}
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold mt-4"
                  data-testid="update-invoice-submit"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {submitting ? "Saving..." : "Save Changes"}
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(`${basePath}/invoices/${id}`)}
                  className="w-full"
                  data-testid="cancel-edit-btn"
                >
                  Cancel
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </form>
    </div>
  );
}
